import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { HubConnectionBuilder, HubConnection } from "@microsoft/signalr";
import { msalInitPromise, msalInstance } from "app/auth/msalInstance";
import { getSignalRConnectionAuth } from "app/shared/api/util";
import { Tooltip, notification } from "antd";
import React, { type JSX, useEffect, useRef, useState } from "react";

async function buildAuthenticatedHubConnection(signalRServiceUrl: string): Promise<HubConnection> {
  await msalInitPromise;
  const { accessTokenFactory, headers } = await getSignalRConnectionAuth(msalInstance);
  return new HubConnectionBuilder()
    .withUrl(signalRServiceUrl, { accessTokenFactory, headers })
    .withAutomaticReconnect()
    .build();
}

function isExpectedSignalRStartCancellation(err: unknown): boolean {
  const anyErr = err as any;
  const name = String(anyErr?.name ?? "").trim();
  const message = String(anyErr?.message ?? anyErr ?? "").trim();
  const combined = `${name} ${message}`.toLowerCase();

  // Common in React dev/StrictMode or quick unmounts: start() begins, then stop() runs during negotiation.
  if (name.toLowerCase() === "aborterror") return true;
  if (combined.includes("stopped during negotiation")) return true;
  return false;
}

export enum PublishedEventType {
  CourseEventPublished = 'CourseEventPublished',
  CourseIntensityEventPublished = 'CourseIntensityEventPublished',
  CourseIntensitySchoolOverrideEventPublished = 'CourseIntensitySchoolOverrideEventPublished',
  CourseIntensityVersionEventPublished = 'CourseIntensityVersionEventPublished',
  CourseLanguageEventPublished = 'CourseLanguageEventPublished',
  TransferEventPublished = 'TransferEventPublished',
  TransferPortEventPublished = 'TransferPortEventPublished',
  TransferPortInstructionEventPublished = 'TransferPortInstructionEventPublished',
  TransferPortTerminalEventPublished = 'TransferPortTerminalEventPublished',
  PackageEventPublished = 'PackageEventPublished',
  PackageProductEventPublished = 'PackageProductEventPublished',
  PackageOfferingProductBreakdownEventPublished = 'PackageOfferingProductBreakdownEventPublished',
  PackageProductBreakdownEventPublished = 'PackageProductBreakdownEventPublished',
  AddOnEventPublished = 'AddOnEventPublished',
  DecommissionDateEventPublished = 'DecommissionDateEventPublished',
  AccommodationEventPublished = 'AccommodationEventPublished',
  AccommodationRoomEventPublished = 'AccommodationRoomEventPublished',
  ProductOfferingEventPublished = 'ProductOfferingEventPublished',
  SchoolEventPublished = 'SchoolEventPublished',
  SchoolClosureEventPublished = 'SchoolClosureEventPublished',
  CentreEventPublished = 'CentreEventPublished',
  DateStrategyEventPublished = 'DateStrategyEventPublished',
  FeeEventPublished = 'FeeEventPublished',
  CancellationFeeEventPublished = 'CancellationFeeEventPublished',
  FeeOfferingEventPublished = 'FeeOfferingEventPublished',
  PricingYearEventPublished = 'PricingYearEventPublished',
  PricePeriodEventPublished = 'PricePeriodEventPublished',
  EarlyBirdEventPublished = 'EarlyBirdEventPublished',
  DiscountEventPublished = 'DiscountEventPublished',
  CommissionGroupEventPublished = 'CommissionGroupEventPublished'
}

export interface EventData {
  eventType: PublishedEventType;
  actionType: string;
  divisionId?: number;
  sequence: number;
  productType?: string;
  schoolId?: number;
}

export const SIGNALR_MATCH_ALL_DIVISIONS = -1;

export function useSignalREventError()
{
  const connectionRef = React.useRef<HubConnection | null>(null);
  const [api, contextHolder] = notification.useNotification();
  const handlerRef = React.useRef<((error: string) => void) | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const signalRServiceUrl = `${import.meta.env.VITE_SIGNALR_SERVICE_URL}/${import.meta.env.VITE_SIGNALR_ERROR_INSTANCE}`;

    void (async () => {
      if (connectionRef.current) return;

      try {
        const connection = await buildAuthenticatedHubConnection(signalRServiceUrl);
        if (cancelled) {
          await connection.stop();
          return;
        }

        connectionRef.current = connection;

        const registerHandlers = () => {
          // Ensure we don't duplicate handlers across reconnects.
          if (handlerRef.current) {
            connection.off("ErrorEventPublished", handlerRef.current);
          }
          const handler = (error: string) => {
            console.log("Event Published:", error);

            api["error"]({
              message: 'Publishing Service Error',
              description: `This publishing service has encounter the following error ${error}`
            });
          };
          handlerRef.current = handler;
          connection.on("ErrorEventPublished", handler);
        };

        // Register handlers initially
        registerHandlers();

        // Re-register on reconnect
        connection.onreconnected(() => {
          console.log("SignalR reconnected, re‑attaching handlers");
          registerHandlers();
        });

        await connection.start();
        if (cancelled) return;
        console.log("Connected to SignalR hub");
      } catch (err) {
        if (cancelled) return;
        if (isExpectedSignalRStartCancellation(err)) {
          console.debug("SignalR start cancelled during negotiation (expected in dev/unmount).");
          return;
        }
        console.error("Connection failed:", err);
      }
    })();

    return () => {
      // Only stop when component unmounts
      cancelled = true;
      if (connectionRef.current) {
        if (handlerRef.current) {
          connectionRef.current.off("ErrorEventPublished", handlerRef.current);
          handlerRef.current = null;
        }
        void connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, []); // empty dependency array → run once

  return contextHolder;
}

export function useSignalREvent(
  eventNames: PublishedEventType[],
  onEvent: (eventType: EventData) => void | Promise<void>,
  divisionId?: number,
  schoolId?: number,
  productType?: string
) {
  const connectionRef = React.useRef<HubConnection | null>(null);
  const [status, setStatus] = React.useState< "connected" | "disconnected" | "reconnecting" >("disconnected");
  const [lastEventReceived, setLastEventReceived] = React.useState<number>(0);
  const handlersRef = useRef(new Map<PublishedEventType, (eventData: EventData) => void>());
  const refreshInFlightRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const latestEventRef = useRef<EventData | null>(null);

  const divisionIdRef = useRef<number|undefined>(divisionId);
  const schoolIdRef = useRef<number|undefined>(schoolId); 
  const productTypeRef = useRef<string|undefined>(productType); 
  const onEventRef = useRef(onEvent);
  
  React.useEffect(() => { 
    divisionIdRef.current = divisionId;
    schoolIdRef.current = schoolId;
    productTypeRef.current = productType;
    onEventRef.current = onEvent; 
  }, [divisionId, schoolId, productType, onEvent]);

  React.useEffect(() => {
    let cancelled = false;
    const signalRServiceUrl = `${import.meta.env.VITE_SIGNALR_SERVICE_URL}/${import.meta.env.VITE_SIGNALR_INSTANCE}`;

    void (async () => {
      if (connectionRef.current) return;

      try {
        const connection = await buildAuthenticatedHubConnection(signalRServiceUrl);
        if (cancelled) {
          await connection.stop();
          return;
        }

        connectionRef.current = connection;

        const registerHandlers = () => {
          eventNames.forEach(eventName => {
            const existing = handlersRef.current.get(eventName);
            if (existing) {
              connection.off(eventName, existing);
            }

            const handler = (eventData: EventData) => {
              const divScope = divisionIdRef.current;
              const matchesDivision =
                divScope === SIGNALR_MATCH_ALL_DIVISIONS
                  ? true
                  : eventData.divisionId == null || eventData.divisionId === (divScope ?? null);
              const matchesSchool = eventData.schoolId == null || eventData.schoolId === (schoolIdRef.current ?? null);
              const matchesProductType = eventData.productType == null || eventData.productType === (productTypeRef.current ?? "");

              // console.log(`Division: ${eventData.divisionId} - ${(divisionIdRef.current ?? null)} - ${matchesDivision}`);
              // console.log(`School: ${eventData.schoolId} - ${(schoolIdRef.current ?? null)} - ${matchesSchool}`);
              // console.log(`Product Type: ${eventData.productType} - ${(productTypeRef.current ?? "")} - ${matchesProductType}`);
              console.log('Event Data Recieved!');
              console.log(eventData);

              if (!(matchesDivision && matchesSchool && matchesProductType)) return;

              // Prevent "loading forever" scenarios when multiple events fire while a refresh is already running.
              // We allow only one in-flight refresh and coalesce additional events into one more refresh.
              latestEventRef.current = eventData;
              setLastEventReceived(Date.now());

              if (refreshInFlightRef.current) {
                pendingRefreshRef.current = true;
                return;
              }

              const run = async (evt: EventData) => {
                // Single-flight: only one refresh at a time.
                refreshInFlightRef.current = true;
                try {
                  await Promise.resolve(onEventRef.current(evt));
                } finally {
                  refreshInFlightRef.current = false;
                }

                // Coalesce: if any events arrived while we were refreshing, run once more with the latest.
                if (pendingRefreshRef.current) {
                  pendingRefreshRef.current = false;
                  const latest = latestEventRef.current ?? evt;
                  return run(latest);
                }
              };

              // Start the refresh without blocking SignalR handler stack.
              void Promise.resolve().then(() => run(eventData));
            };

            handlersRef.current.set(eventName, handler);
            connection.on(eventName, handler);
          });
        };

        // Register handlers initially
        registerHandlers();

        // Re-register on reconnect
        connection.onreconnected(() => {
          console.log("SignalR reconnected, re‑attaching handlers");
          registerHandlers();
          setStatus("connected");
        });

        connection.onreconnecting(() => setStatus("reconnecting"));
        connection.onclose(() => setStatus("disconnected"));

        await connection.start();
        if (cancelled) return;
        console.log("Connected to SignalR hub");
        setStatus("connected");
      } catch (err) {
        if (cancelled) return;
        if (isExpectedSignalRStartCancellation(err)) {
          console.debug("SignalR start cancelled during negotiation (expected in dev/unmount).");
          return;
        }
        console.error("Connection failed:", err);
        setStatus("disconnected");
      }
    })();

    return () => {
      // Only stop when component unmounts
      cancelled = true;
      if (connectionRef.current) {
        for (const [eventName, handler] of handlersRef.current.entries()) {
          connectionRef.current.off(eventName, handler);
        }
        handlersRef.current.clear();
        void connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, []); // empty dependency array → run once

  return { status, lastEventReceived };
}

export function StatusIcon({
  status,
  lastEventReceived,
  unpublishedChanges,
  style,
  onManualRefresh
}: {
  status: "connected" | "disconnected" | "reconnecting",
  lastEventReceived?: number,
  unpublishedChanges?: boolean,
  style?: React.CSSProperties,
  onManualRefresh?: () => void
}) {
  const [flash, setFlash] = useState(false);
  const [staleChanges, setStaleChanges] = useState(false);
  const [retrySecondsLeft, setRetrySecondsLeft] = useState<number | null>(null);
  const autoRefreshDoneRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  const retryIntervalRef = useRef<number | null>(null);

  const clearRetryTimers = React.useCallback(() => {
    if (retryTimerRef.current != null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (retryIntervalRef.current != null) {
      window.clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
    setRetrySecondsLeft(null);
  }, []);

  useEffect(() => {
    if (lastEventReceived) {
      setFlash(true);
      setStaleChanges(false);
      const timer = setTimeout(() => setFlash(false), 800);
      return () => clearTimeout(timer);
    }
  }, [lastEventReceived]);

  // Detect if unpublished changes have been pending > 30 seconds
  useEffect(() => {
    if (!unpublishedChanges) {
      setStaleChanges(false);
      autoRefreshDoneRef.current = false;
      clearRetryTimers();
      return;
    }

    const timer = setTimeout(() => {
      setStaleChanges(true);
    }, import.meta.env.VITE_SIGNALR_REFRESH_TIMEOUT); 

    return () => clearTimeout(timer);
  }, [unpublishedChanges, lastEventReceived, clearRetryTimers]);

  // After we enter the "stale" state, show a countdown and auto-refresh once.
  useEffect(() => {
    if (!unpublishedChanges || !staleChanges) {
      clearRetryTimers();
      return;
    }
    if (autoRefreshDoneRef.current) {
      return;
    }

    const RETRY_DELAY_MS = import.meta.env.VITE_SIGNALR_REFRESH_RETRY;
    const startSeconds = Math.ceil(RETRY_DELAY_MS / 1000);
    setRetrySecondsLeft(startSeconds);

    retryIntervalRef.current = window.setInterval(() => {
      setRetrySecondsLeft((prev) => {
        if (prev == null) return prev;
        return prev > 0 ? prev - 1 : 0;
      });
    }, 1000);

    retryTimerRef.current = window.setTimeout(() => {
      autoRefreshDoneRef.current = true;
      clearRetryTimers();
      try {
        if (typeof onManualRefresh === "function") onManualRefresh();
        else window.location.reload();
      } catch {
        // ignore
      }
    }, RETRY_DELAY_MS);

    return () => {
      clearRetryTimers();
    };
  }, [unpublishedChanges, staleChanges, onManualRefresh, clearRetryTimers]);

  let icon:JSX.Element = (
    <LoadingOutlined
      style={{ color: "orange", fontSize: 20 }}
      className={flash ? "spin-flash" : ""}
    />
  );
  let text:JSX.Element|string = "Connected: Publishing Changes";
  let tooltipText =
    "There are some unpublished changes. Affected products are locked and cannot be edited. Changes will show automatically once processed successfully.";

  if (unpublishedChanges && staleChanges) {
    icon = (
      <LoadingOutlined
        style={{ color: "orange", fontSize: 20 }}
        className={flash ? "spin-flash" : ""}
      />
    );
    text = (
      <>
        Connected: Publishing Changes is taking longer than expected.
        {retrySecondsLeft != null ? (
          <> Retrying refresh in {retrySecondsLeft}s…</>
        ) : null}{" "}
        <a
          className="pointer"
          onClick={() => {
            autoRefreshDoneRef.current = true;
            clearRetryTimers();
            onManualRefresh !== undefined ? onManualRefresh() : window.location.reload();
          }}
        >
          Try to refresh page manually
        </a>
      </>
    )
    tooltipText =
      "Publishing seems stalled. Click to refresh the page to try and retrieve the latest data.";
  }
  else if (!unpublishedChanges) {
    switch (status) {
      case "connected":
        icon = (
          <CheckCircleOutlined
            style={{ color: "green", fontSize: 20 }}
            className={flash ? "spin-flash" : ""}
          />
        );
        text = "Connected: Up to Date";
        tooltipText = "Connected to SignalR Hub for realtime updates.";
        break;

      case "disconnected":
        icon = (
          <CloseCircleOutlined
            style={{ color: "red", fontSize: 20 }}
          />
        );
        text = "Not Connected";
        tooltipText = "Disconnected from SignalR Hub. Realtime updates not available.";
        break;

      case "reconnecting":
        icon = (
          <LoadingOutlined
            style={{ color: "orange", fontSize: 20 }}
            spin
          />
        );
        text = "Reconnecting…";
        tooltipText = "Reconnecting to SignalR Hub.";
        break;
    }
  }

  return (
    <Tooltip title={tooltipText}>
      <div style={{ display: "flex", alignItems: "center", ...style }}>
        {icon}
        <span style={{ marginLeft: 8, fontWeight: 500 }}>{text}</span>
      </div>
    </Tooltip>
  );
}
